import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useCartActionMutation } from 'src/store/api/cart/catsApiSlice';
import { isAuthSelector } from 'src/store/reducers/auth';
import { cartAddItem, cartRemoveItem, cartReset, cartUpdateItems } from 'src/store/reducers/cart';
import { RootState } from 'src/store/store';
import { CartItemType } from 'src/types/cart/CartItem';
import {
    mapServerCartItems,
    storageAddCartItem,
    storageCartReset,
    storageRemoveCartItem,
    storageSetCartItems,
} from 'src/utils/cart';
import { getCartErrorMessage } from 'src/utils/cartErrors';

const CART_ERROR_TOAST_ID = 'cart-action-error';

const useCartAction = (item?: CartItemType) => {
    const dispatch = useDispatch();
    const store = useStore<RootState>();
    const { t } = useTranslation('cart');
    const isAuth = useSelector(isAuthSelector);
    const [sendCartAction] = useCartActionMutation();

    const qty = useSelector((state: RootState) => {
        if (item) {
            const cartState = state.cart.items || [];
            const itemInCart = cartState.find((itm: CartItemType) => itm.slug === item.slug);
            return itemInCart ? itemInCart.qty : 0;
        }
        return 0;
    });

    // Apply the authoritative server cart to Redux + storage. Skipped when the
    // user logged out while the request was in flight, so a stale authenticated
    // response can never repopulate the cart after logout.
    const reconcile = useCallback((serverItems: unknown) => {
        if (!store.getState().auth.access) return;
        const items = mapServerCartItems(serverItems);
        dispatch(cartUpdateItems({ items }));
        storageSetCartItems(items);
    }, [dispatch, store]);

    const rollback = useCallback((previousItems: CartItemType[], error: unknown) => {
        dispatch(cartUpdateItems({ items: previousItems }));
        storageSetCartItems(previousItems);
        toast.error(getCartErrorMessage(error, t), { toastId: CART_ERROR_TOAST_ID });
    }, [dispatch, t]);

    const cartAddPress = useCallback(async () => {
        if (!item) return;
        const previousItems = store.getState().cart.items;

        dispatch(cartAddItem({ cartItem: item }));
        storageAddCartItem(item);

        if (!isAuth) return;
        try {
            const payload = await sendCartAction({ action: 'add', slug: item.slug, qty: 1 }).unwrap();
            reconcile(payload?.items);
        } catch (error) {
            rollback(previousItems, error);
        }
    }, [item, store, dispatch, isAuth, sendCartAction, reconcile, rollback]);

    const cartRemovePress = useCallback(async () => {
        if (!item) return;
        const previousItems = store.getState().cart.items;

        dispatch(cartRemoveItem({ slug: item.slug }));
        storageRemoveCartItem(item.slug);

        if (!isAuth) return;
        try {
            const payload = await sendCartAction({ action: 'remove', slug: item.slug }).unwrap();
            reconcile(payload?.items);
        } catch (error) {
            rollback(previousItems, error);
        }
    }, [item, store, dispatch, isAuth, sendCartAction, reconcile, rollback]);

    const cartResetPress = useCallback(async () => {
        const previousItems = store.getState().cart.items;

        dispatch(cartReset());
        storageCartReset();

        if (!isAuth) return;
        try {
            const payload = await sendCartAction({ action: 'reset' }).unwrap();
            reconcile(payload?.items);
        } catch (error) {
            rollback(previousItems, error);
        }
    }, [store, dispatch, isAuth, sendCartAction, reconcile, rollback]);

    return { qty, cartAddPress, cartRemovePress, cartResetPress };
};

export default useCartAction;
